import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { projectsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  ArrowLeft, ArrowRight, Plus, Trash2, CheckCircle, Loader2,
  FileText, Calendar, Target, DollarSign, AlertTriangle, ClipboardList, HelpCircle
} from 'lucide-react';
import { RISK_LEVELS, MILESTONE_STATUSES } from '@/lib/constants';

const STEPS = [
  { id: 1, name: 'Basics', icon: FileText },
  { id: 2, name: 'Scope', icon: Target },
  { id: 3, name: 'Schedule', icon: Calendar },
  { id: 4, name: 'Budget/Sprint', icon: DollarSign },
  { id: 5, name: 'Risks', icon: AlertTriangle },
  { id: 6, name: 'Additional', icon: ClipboardList },
];

const ProjectWizard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    // Step 1: Basics
    name: '',
    methodology: 'predictive',
    pm_name: user?.name || '',
    sponsor_name: '',
    start_date: '',
    end_date: '',
    team_size: '',
    // Step 2: Scope
    scope_summary: '',
    deliverables: [],
    // Step 3: Schedule
    milestones: [],
    // Step 4: Budget (Predictive) or Sprint (Agile)
    skip_budget: false,
    bac: '',
    contingency_reserve: '',
    management_reserve: '',
    evm_enabled: false,
    sprint_length: '4',
    initial_velocity: '',
    backlog_size: '',
    // Step 5: Risks
    risks: [],
    // Step 6: Additional
    objectives: '',
    assumptions: '',
    constraints: '',
    notes: '',
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addDeliverable = () => {
    setFormData(prev => ({
      ...prev,
      deliverables: [...prev.deliverables, { name: '', status: 'not_started' }]
    }));
    // Focus the new input after render
    setTimeout(() => {
      const inputs = document.querySelectorAll('[data-deliverable-input]');
      const lastInput = inputs[inputs.length - 1];
      if (lastInput) lastInput.focus();
    }, 50);
  };

  const updateDeliverable = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.deliverables];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, deliverables: updated };
    });
  };

  const removeDeliverable = (index) => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index)
    }));
  };

  const addMilestone = () => {
    setFormData(prev => ({
      ...prev,
      milestones: [...prev.milestones, { name: '', target_date: '', status: 'not_started' }]
    }));
    // Focus the new input after render
    setTimeout(() => {
      const inputs = document.querySelectorAll('[data-milestone-input]');
      const lastInput = inputs[inputs.length - 1];
      if (lastInput) lastInput.focus();
    }, 50);
  };

  const updateMilestone = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.milestones];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, milestones: updated };
    });
  };

  const removeMilestone = (index) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index)
    }));
  };

  // Enhancement 10: New risks at TOP
  const addRisk = () => {
    setFormData(prev => ({
      ...prev,
      risks: [{ description: '', probability: 'medium', impact: 'medium', mitigation: '' }, ...prev.risks]
    }));
    // Focus the new risk input after render
    setTimeout(() => {
      const inputs = document.querySelectorAll('[data-risk-input]');
      if (inputs[0]) inputs[0].focus();
    }, 50);
  };

  const updateRisk = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.risks];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, risks: updated };
    });
  };

  const removeRisk = (index) => {
    setFormData(prev => ({
      ...prev,
      risks: prev.risks.filter((_, i) => i !== index)
    }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        // Validate each required field individually for better error messaging
        const missingFields = [];
        if (!formData.name?.trim()) missingFields.push('Project Name');
        if (!formData.pm_name?.trim()) missingFields.push('Project Manager');
        if (!formData.sponsor_name?.trim()) missingFields.push('Project Sponsor');
        if (!formData.start_date) missingFields.push('Start Date');
        if (!formData.end_date) missingFields.push('End Date');
        
        if (missingFields.length > 0) {
          setError(`Please fill in: ${missingFields.join(', ')}`);
          return false;
        }
        
        // BUG 5 FIX: Validate end date is after start date
        if (formData.start_date && formData.end_date) {
          if (new Date(formData.end_date) <= new Date(formData.start_date)) {
            setError('End date must be after start date');
            return false;
          }
        }
        break;
      default:
        break;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 6));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    setError('');

    try {
      const projectData = {
        name: formData.name,
        methodology: formData.methodology,
        pm_name: formData.pm_name,
        sponsor_name: formData.sponsor_name,
        start_date: formData.start_date,
        end_date: formData.end_date,
        team_size: formData.team_size ? parseInt(formData.team_size) : null,
        scope_summary: formData.scope_summary || null,
        deliverables: formData.deliverables.filter(d => d.name),
        milestones: formData.milestones.filter(m => m.name && m.target_date),
        risks: formData.risks.filter(r => r.description),
        // Handle BAC for both predictive and agile (if enabled)
        bac: (formData.methodology === 'predictive' && formData.bac) || 
             (formData.methodology === 'agile' && formData.agile_budget_enabled && formData.bac) 
             ? parseFloat(formData.bac) : null,
        evm_enabled: formData.methodology === 'predictive' ? formData.evm_enabled : 
                     (formData.agile_budget_enabled && formData.bac ? true : false),
        contingency_reserve: formData.methodology === 'predictive' && formData.contingency_reserve ? parseFloat(formData.contingency_reserve) : null,
        management_reserve: formData.methodology === 'predictive' && formData.management_reserve ? parseFloat(formData.management_reserve) : null,
        sprint_length: formData.methodology === 'agile' ? parseInt(formData.sprint_length) : null,
        initial_velocity: formData.methodology === 'agile' && formData.initial_velocity ? parseInt(formData.initial_velocity) : null,
        backlog_size: formData.methodology === 'agile' && formData.backlog_size ? parseInt(formData.backlog_size) : null,
        objectives: formData.objectives || null,
        assumptions: formData.assumptions || null,
        constraints: formData.constraints || null,
        notes: formData.notes || null,
      };

      const response = await projectsAPI.create(projectData);
      navigate(`/projects/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <Step1Basics formData={formData} updateFormData={updateFormData} />;
      case 2:
        return (
          <Step2Scope
            formData={formData}
            updateFormData={updateFormData}
            addDeliverable={addDeliverable}
            updateDeliverable={updateDeliverable}
            removeDeliverable={removeDeliverable}
          />
        );
      case 3:
        return (
          <Step3Schedule
            formData={formData}
            addMilestone={addMilestone}
            updateMilestone={updateMilestone}
            removeMilestone={removeMilestone}
          />
        );
      case 4:
        return formData.methodology === 'predictive'
          ? <Step4Budget formData={formData} updateFormData={updateFormData} />
          : <Step4Sprint formData={formData} updateFormData={updateFormData} />;
      case 5:
        return (
          <Step5Risks
            formData={formData}
            addRisk={addRisk}
            updateRisk={updateRisk}
            removeRisk={removeRisk}
          />
        );
      case 6:
        return <Step6Additional formData={formData} updateFormData={updateFormData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold">Create New Project</h1>
          <p className="text-slate-600">Set up your project in a few easy steps</p>
        </div>

        {/* Progress Stepper */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isComplete = currentStep > step.id;
              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-2 ${isActive ? 'text-blue-600' : isComplete ? 'text-green-600' : 'text-slate-400'}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${isActive ? 'bg-blue-100 text-blue-600' : isComplete ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}
                  >
                    {isComplete ? <CheckCircle className="h-4 w-4" /> : step.id}
                  </div>
                  <span className="hidden md:inline text-sm font-medium">{step.name}</span>
                </div>
              );
            })}
          </div>
          <Progress value={(currentStep / 6) * 100} className="h-2" />
        </div>

        {/* Step Content */}
        <Card>
          <CardHeader>
            <CardTitle>Step {currentStep}: {STEPS[currentStep - 1].name}</CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 1}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          {currentStep < 6 ? (
            <Button onClick={handleNext} data-testid="wizard-next-btn">
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} data-testid="wizard-submit-btn">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Project
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const Step1Basics = ({ formData, updateFormData }) => (
  <div className="space-y-6">
    <div className="space-y-2">
      <Label htmlFor="name">Project Name *</Label>
      <Input
        id="name"
        value={formData.name}
        onChange={(e) => updateFormData('name', e.target.value)}
        placeholder="e.g., Website Redesign"
        data-testid="project-name-input"
      />
    </div>

    <div className="space-y-2">
      <Label>Methodology *</Label>
      <RadioGroup
        value={formData.methodology}
        onValueChange={(value) => updateFormData('methodology', value)}
        className="flex gap-4"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="predictive" id="predictive" />
          <Label htmlFor="predictive" className="cursor-pointer">
            <span className="font-medium">Predictive (Waterfall)</span>
            <span className="text-slate-500 text-sm block">Traditional project management with milestones</span>
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="agile" id="agile" />
          <Label htmlFor="agile" className="cursor-pointer">
            <span className="font-medium">Agile</span>
            <span className="text-slate-500 text-sm block">Sprint-based with velocity tracking</span>
          </Label>
        </div>
      </RadioGroup>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="pm_name">Project Manager *</Label>
        <Input
          id="pm_name"
          value={formData.pm_name}
          onChange={(e) => updateFormData('pm_name', e.target.value)}
          placeholder="Your name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="sponsor_name">Project Sponsor *</Label>
        <Input
          id="sponsor_name"
          value={formData.sponsor_name}
          onChange={(e) => updateFormData('sponsor_name', e.target.value)}
          placeholder="Sponsor name"
        />
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="start_date">Start Date *</Label>
        <Input
          id="start_date"
          type="date"
          value={formData.start_date}
          onChange={(e) => updateFormData('start_date', e.target.value)}
          onBlur={(e) => updateFormData('start_date', e.target.value)}
          onInput={(e) => updateFormData('start_date', e.target.value)}
        />
      </div>
      <div className="space-y-2">
        {/* Enhancement 6: Change label for Agile */}
        <Label htmlFor="end_date">{formData.methodology === 'agile' ? 'Target End Date *' : 'End Date *'}</Label>
        <Input
          id="end_date"
          type="date"
          value={formData.end_date}
          onChange={(e) => updateFormData('end_date', e.target.value)}
          onBlur={(e) => updateFormData('end_date', e.target.value)}
          onInput={(e) => updateFormData('end_date', e.target.value)}
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="team_size">Team Size (optional)</Label>
      <Input
        id="team_size"
        type="number"
        value={formData.team_size}
        onChange={(e) => updateFormData('team_size', e.target.value)}
        placeholder="Number of team members"
      />
    </div>
  </div>
);

const Step2Scope = ({ formData, updateFormData, addDeliverable, updateDeliverable, removeDeliverable }) => (
  <div className="space-y-6">
    <div className="space-y-2">
      <Label htmlFor="scope_summary">Scope Summary</Label>
      <Textarea
        id="scope_summary"
        value={formData.scope_summary}
        onChange={(e) => updateFormData('scope_summary', e.target.value)}
        placeholder="Describe the project scope in 2-3 sentences..."
        rows={4}
      />
    </div>

    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Key Deliverables</Label>
        <Button type="button" variant="outline" size="sm" onClick={addDeliverable}>
          <Plus className="h-4 w-4 mr-1" />
          Add Deliverable
        </Button>
      </div>
      {formData.deliverables.map((deliverable, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            data-deliverable-input
            value={deliverable.name}
            onChange={(e) => updateDeliverable(index, 'name', e.target.value)}
            placeholder={`Deliverable ${index + 1}`}
          />
          <Button variant="ghost" size="icon" onClick={() => removeDeliverable(index)}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ))}
      {formData.deliverables.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-4">No deliverables added yet</p>
      )}
    </div>
  </div>
);

const Step3Schedule = ({ formData, addMilestone, updateMilestone, removeMilestone }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <Label>Key Milestones</Label>
        <p className="text-sm text-slate-500">Define major project milestones</p>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={addMilestone}>
        <Plus className="h-4 w-4 mr-1" />
        Add Milestone
      </Button>
    </div>

    {/* Enhancement 9: Helper note */}
    <p className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
      💡 You can add more milestones later from the project view.
    </p>

    <div className="space-y-4">
      {formData.milestones.map((milestone, index) => (
        <Card key={index} className="p-4">
          <div className="space-y-4">
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-4">
                <Input
                  data-milestone-input
                  value={milestone.name}
                  onChange={(e) => updateMilestone(index, 'name', e.target.value)}
                  placeholder="Milestone name"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="date"
                    value={milestone.target_date}
                    onChange={(e) => updateMilestone(index, 'target_date', e.target.value)}
                    onBlur={(e) => updateMilestone(index, 'target_date', e.target.value)}
                    onInput={(e) => updateMilestone(index, 'target_date', e.target.value)}
                  />
                  <Select
                    value={milestone.status}
                    onValueChange={(value) => updateMilestone(index, 'status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MILESTONE_STATUSES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => removeMilestone(index)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          </div>
        </Card>
      ))}
      {formData.milestones.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-8">No milestones added yet</p>
      )}
    </div>
  </div>
);

const Step4Budget = ({ formData, updateFormData }) => (
  <TooltipProvider>
    <div className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="font-medium text-blue-900">Budget Information (Predictive Project)</h3>
        <p className="text-sm text-blue-700">Track your project budget and earned value metrics.</p>
      </div>

      {/* Skip Budget Toggle */}
      <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2">
          <div>
            <Label htmlFor="skip_budget" className="font-medium">Skip Budget Tracking</Label>
            <p className="text-sm text-slate-500">Not all projects have a defined budget</p>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-4 w-4 text-amber-600 cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-sm">If you skip budget tracking, EVM statistics (SPI, CPI, EAC, VAC) will not be available for this project. You can always add budget information later.</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <Switch
          id="skip_budget"
          checked={formData.skip_budget || false}
          onCheckedChange={(checked) => {
            updateFormData('skip_budget', checked);
            if (checked) {
              updateFormData('evm_enabled', false);
              updateFormData('bac', '');
            }
          }}
        />
      </div>

      {!formData.skip_budget && (
        <>
          {/* BAC Input First - More intuitive flow */}
          <div className="space-y-2">
            <Label htmlFor="bac">Budget at Completion (BAC)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
              <Input
                id="bac"
                type="number"
                value={formData.bac}
                onChange={(e) => {
                  updateFormData('bac', e.target.value);
                  // Auto-enable EVM when BAC is entered
                  if (e.target.value && !formData.evm_enabled) {
                    updateFormData('evm_enabled', true);
                  }
                }}
                placeholder="Total approved budget"
                className="pl-7"
              />
            </div>
            <p className="text-xs text-slate-500">Enter your project&apos;s total approved budget to enable EVM tracking</p>
          </div>

          {/* EVM Toggle - Show recommendation based on BAC */}
          <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${formData.bac && !formData.evm_enabled ? 'bg-blue-50 border-blue-300' : 'bg-slate-50 border-transparent'}`}>
            <div className="flex items-center gap-2">
              <div>
                <Label htmlFor="evm_enabled" className="font-medium">Enable EVM Tracking</Label>
                <p className="text-sm text-slate-500">Calculate SPI, CPI, EAC, and other earned value metrics</p>
                {formData.bac && !formData.evm_enabled && (
                  <p className="text-xs text-blue-600 mt-1">💡 Recommended: You have a BAC set - enable EVM for detailed performance tracking</p>
                )}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">Earned Value Management requires a Budget at Completion (BAC) to calculate schedule and cost performance indices.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Switch
              id="evm_enabled"
              checked={formData.evm_enabled || false}
              onCheckedChange={(checked) => updateFormData('evm_enabled', checked)}
              data-testid="wizard-evm-toggle"
            />
          </div>

          {formData.evm_enabled && !formData.bac && (
            <p className="text-xs text-amber-600">⚠️ BAC is required for EVM calculations - please enter a budget above</p>
          )}

          {formData.evm_enabled && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="contingency_reserve">Contingency Reserve (optional)</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Funds set aside for identified risks that may occur. Used when known risks materialize.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    id="contingency_reserve"
                    type="number"
                    value={formData.contingency_reserve}
                    onChange={(e) => updateFormData('contingency_reserve', e.target.value)}
                    placeholder="0"
                    className="pl-7"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="management_reserve">Management Reserve (optional)</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Funds set aside for unknown risks and unplanned changes. Controlled by management, not the project manager.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    id="management_reserve"
                    type="number"
                    value={formData.management_reserve}
                    onChange={(e) => updateFormData('management_reserve', e.target.value)}
                    placeholder="0"
                    className="pl-7"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {formData.skip_budget && (
        <div className="p-4 bg-slate-50 rounded-lg text-center text-slate-500">
          <p>Budget tracking is disabled for this project.</p>
          <p className="text-sm mt-1">You can enable it later in project settings.</p>
        </div>
      )}
    </div>
  </TooltipProvider>
);

const Step4Sprint = ({ formData, updateFormData }) => (
  <TooltipProvider>
    <div className="space-y-6">
      <div className="bg-green-50 p-4 rounded-lg">
        <h3 className="font-medium text-green-900">Sprint Setup (Agile Project)</h3>
        <p className="text-sm text-green-700">Configure your sprint settings for velocity tracking</p>
      </div>

      {/* Enhancement 7: Budget toggle for Agile */}
      <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2">
          <div>
            <Label htmlFor="agile_budget_enabled" className="font-medium">Enable Budget Tracking</Label>
            <p className="text-sm text-slate-500">Track budget for this Agile project</p>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-4 w-4 text-amber-600 cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-sm">Agile projects don&apos;t always use budgeting. Enable this if you want to track costs and EVM metrics.</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <Switch
          id="agile_budget_enabled"
          checked={formData.agile_budget_enabled || false}
          onCheckedChange={(checked) => {
            updateFormData('agile_budget_enabled', checked);
            if (!checked) {
              updateFormData('bac', '');
              updateFormData('evm_enabled', false);
            }
          }}
        />
      </div>

      {/* Show BAC field if budget enabled for Agile */}
      {formData.agile_budget_enabled && (
        <div className="space-y-2">
          <Label htmlFor="bac">Budget at Completion (BAC)</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
            <Input
              id="bac"
              type="number"
              value={formData.bac}
              onChange={(e) => updateFormData('bac', e.target.value)}
              placeholder="Total approved budget"
              className="pl-7"
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="sprint_length">Sprint Length</Label>
        <Select
          value={formData.sprint_length}
          onValueChange={(value) => updateFormData('sprint_length', value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 week</SelectItem>
            <SelectItem value="2">2 weeks</SelectItem>
            <SelectItem value="3">3 weeks</SelectItem>
            <SelectItem value="4">4 weeks</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          {/* Enhancement 8: Tooltip for Initial Velocity */}
          <div className="flex items-center gap-2">
            <Label htmlFor="initial_velocity">Initial Velocity Estimate</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-sm">Velocity is the number of story points a team completes per sprint on average.</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Input
            id="initial_velocity"
            type="number"
            value={formData.initial_velocity}
            onChange={(e) => updateFormData('initial_velocity', e.target.value)}
            placeholder="Story points per sprint"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="backlog_size">Total Backlog Size (optional)</Label>
          <Input
            id="backlog_size"
            type="number"
            value={formData.backlog_size}
            onChange={(e) => updateFormData('backlog_size', e.target.value)}
            placeholder="Total story points"
          />
        </div>
      </div>
    </div>
  </TooltipProvider>
);

const Step5Risks = ({ formData, addRisk, updateRisk, removeRisk }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <Label>Initial Risks</Label>
        <p className="text-sm text-slate-500">Identify known project risks</p>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={addRisk}>
        <Plus className="h-4 w-4 mr-1" />
        Add Risk
      </Button>
    </div>

    <div className="space-y-4">
      {formData.risks.map((risk, index) => (
        <Card key={index} className="p-4">
          <div className="space-y-4">
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-4">
                <Textarea
                  data-risk-input
                  value={risk.description}
                  onChange={(e) => updateRisk(index, 'description', e.target.value)}
                  placeholder="Describe the risk..."
                  rows={2}
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Probability</Label>
                    <Select
                      value={risk.probability}
                      onValueChange={(value) => updateRisk(index, 'probability', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RISK_LEVELS.map(l => (
                          <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Impact</Label>
                    <Select
                      value={risk.impact}
                      onValueChange={(value) => updateRisk(index, 'impact', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RISK_LEVELS.map(l => (
                          <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Input
                  value={risk.mitigation}
                  onChange={(e) => updateRisk(index, 'mitigation', e.target.value)}
                  placeholder="Mitigation strategy (optional)"
                />
              </div>
              <Button variant="ghost" size="icon" onClick={() => removeRisk(index)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          </div>
        </Card>
      ))}
      {formData.risks.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-8">No risks added yet</p>
      )}
    </div>
  </div>
);

const Step6Additional = ({ formData, updateFormData }) => (
  <div className="space-y-6">
    <p className="text-sm text-slate-500">These fields are optional but can provide useful context.</p>

    <div className="space-y-2">
      <Label htmlFor="objectives">Project Objectives / Success Criteria</Label>
      <Textarea
        id="objectives"
        value={formData.objectives}
        onChange={(e) => updateFormData('objectives', e.target.value)}
        placeholder="What does success look like?"
        rows={3}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="assumptions">Key Assumptions</Label>
      <Textarea
        id="assumptions"
        value={formData.assumptions}
        onChange={(e) => updateFormData('assumptions', e.target.value)}
        placeholder="List key assumptions..."
        rows={3}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="constraints">Key Constraints</Label>
      <Textarea
        id="constraints"
        value={formData.constraints}
        onChange={(e) => updateFormData('constraints', e.target.value)}
        placeholder="List key constraints..."
        rows={3}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="notes">Additional Notes</Label>
      <Textarea
        id="notes"
        value={formData.notes}
        onChange={(e) => updateFormData('notes', e.target.value)}
        placeholder="Any other information..."
        rows={3}
      />
    </div>
  </div>
);

export default ProjectWizard;
